/**
 * §A.4 · El CRM de leads.
 *
 * ## El embudo arriba, y no por jerarquía visual
 *
 * Las dos cifras que importan son `overdue_actions` y `stale`: una próxima acción con
 * fecha pasada es «una promesa que nos hicimos y no cumplimos», y es lo único que
 * impide que el embudo se convierta en un archivo. Van arriba porque son lo que se
 * hace hoy; la lista de leads es para después de saber a quién llamar.
 *
 * ## Convertir existe para no cometer el error más caro del CRM
 *
 * «Dar de alta el club a mano y que el lead se quede en `demo` para siempre,
 * falseando el embudo entero.» `POST /convert` enlaza `crm_leads.academy_id` y mueve
 * la etapa en la misma transacción, así que lo que hay que evitar es que alguien cree
 * el club por el otro lado. El botón vive **en el lead de prueba o ganado** y se
 * explica.
 *
 * ## Convertir deja el lead en «de prueba», no en «ganado» · 08-10-2026
 *
 * Convertir abre los días de prueba del club. Antes el lead pasaba a «ganado» y el
 * embudo contaba como ganados a clubes que no habían pagado nada; ahora queda en
 * «de prueba» y pasa a «ganado» solo con su primer pago anotado.
 *
 * Y no se vuelve a teclear nada: del formulario solo sale lo que el lead no puede
 * saber —la URL del club y a quién se invita—.
 *
 * ## Pasar a «perdido» sin motivo no se bloquea
 *
 * El contrato es explícito: «el motivo importa, pero exigirlo a las ocho de la tarde
 * consigue que nadie mueva la etapa, y un embudo desactualizado es peor que uno con
 * motivos flojos». Así que se pide y no se obliga.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { cuenta, fecha, fechaParaInput, numero } from '@/shared/ui/formato';
import {
  accionVencida,
  CANALES,
  claseDeEtapa,
  comoUrl,
  etapaCerrada,
  ETAPAS,
  etiquetaDeCanal,
  etiquetaDeEtapa,
  type CanalDeLead,
  type Embudo,
  type EtapaDeLead,
  type Lead,
} from '@/backoffice/tiposDeLeads';

interface DisciplinaDelCatalogo {
  readonly id: string;
  readonly code: string;
  readonly name: string;
}

/** Las cuatro disciplinas de Kiap: las casillas del lead y la conversión. */
function useCatalogoDeDisciplinas() {
  return useQuery({
    queryKey: ['disciplinas'],
    queryFn: async () => {
      const { data, error } = await api.v1.disciplines.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as DisciplinaDelCatalogo[];
    },
  });
}

/**
 * Qué enseña el club, en casillas · 03-11-2026. Por código, como las guarda el lead;
 * al convertirlo son las disciplinas del club, y su primer grupo sale con la suya.
 */
function CasillasDeDisciplinas({
  marcadas,
  alCambiar,
}: {
  marcadas: readonly string[];
  alCambiar: (codigos: string[]) => void;
}) {
  const catalogo = useCatalogoDeDisciplinas();
  return (
    <fieldset className="opciones">
      <legend>Qué enseña</legend>
      <div className="casillas-en-linea">
        {(catalogo.data ?? []).map((d) => (
          <label key={d.code} className="opcion opcion-fila">
            <input
              type="checkbox"
              checked={marcadas.includes(d.code)}
              onChange={(e) =>
                alCambiar(
                  e.target.checked
                    ? [...marcadas, d.code]
                    : marcadas.filter((c) => c !== d.code)
                )
              }
            />
            <span>{d.name}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/** Hoy en `YYYY-MM-DD` **local**, para comparar con una fecha sin hora. */
const hoy = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
};

export function Comercial({
  alAbrirClub,
}: {
  alAbrirClub: (clubId: string) => void;
}) {
  const cache = useQueryClient();
  const [etapa, setEtapa] = useState('');
  const [abierto, setAbierto] = useState<Lead | null>(null);
  const [creando, setCreando] = useState(false);
  const [convirtiendo, setConvirtiendo] = useState<Lead | null>(null);
  const [problema, setProblema] = useState<Problema | null>(null);
  const catalogo = useCatalogoDeDisciplinas();
  const nombreDe = (codigo: string) =>
    catalogo.data?.find((d) => d.code === codigo)?.name ?? codigo;

  const embudo = useQuery({
    queryKey: ['admin', 'embudo'],
    queryFn: async () => {
      const { data, error } = await api.v1.admin.leads.pipeline.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Embudo;
    },
  });

  const leads = useQuery({
    queryKey: ['admin', 'leads', etapa],
    queryFn: async () => {
      const { data, error } = await api.v1.admin.leads.get({
        query: {
          ...(etapa === '' ? {} : { stage: etapa as EtapaDeLead }),
          limit: 200,
        },
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Lead[];
    },
  });

  const refrescar = () => {
    void cache.invalidateQueries({ queryKey: ['admin', 'leads'] });
    void cache.invalidateQueries({ queryKey: ['admin', 'embudo'] });
  };

  const mover = useMutation({
    mutationFn: async (cambio: { id: string } & CambioDeLead) => {
      const { id, ...resto } = cambio;
      const r = await api.v1.admin.leads({ id }).patch(resto);
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data as unknown as Lead;
    },
    onSuccess: (l) => {
      setAbierto(l);
      refrescar();
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  if (creando) {
    return (
      <AltaDeLead
        alCerrar={() => setCreando(false)}
        alCrear={() => {
          setCreando(false);
          refrescar();
        }}
      />
    );
  }

  if (convirtiendo) {
    return (
      <ConversionDeLead
        lead={convirtiendo}
        alCerrar={() => setConvirtiendo(null)}
        alConvertir={() => {
          setConvirtiendo(null);
          setAbierto(null);
          refrescar();
        }}
      />
    );
  }

  return (
    <section>
      <div className="titulo-fila">
        <h1>Comercial</h1>
        <button type="button" onClick={() => setCreando(true)}>
          Anotar un lead
        </button>
      </div>

      {problema && <PanelError problema={problema} />}

      {/* ── El embudo ─────────────────────────────────────────────────────── */}
      {embudo.isPending && <Cargando filas={2} etiqueta="Cargando el embudo" />}

      {embudo.isError && (
        <PanelError
          problema={aProblema(embudo.error)}
          reintentar={() => void embudo.refetch()}
        />
      )}

      {embudo.data && (
        <>
          {/* Lo que hay que hacer hoy, delante de todo lo demás. */}
          {(() => {
            const vencidas = embudo.data.stages.reduce(
              (s, e) => s + e.overdue_actions,
              0
            );
            const paradas = embudo.data.stages.reduce((s, e) => s + e.stale, 0);
            if (vencidas === 0 && paradas === 0) return null;
            return (
              <div
                className={vencidas > 0 ? 'aviso aviso-grave' : 'aviso'}
                role="status"
              >
                {vencidas > 0 && (
                  <p>
                    <strong>
                      {cuenta(
                        vencidas,
                        'acción pendiente',
                        'acciones pendientes'
                      )}{' '}
                      con la fecha pasada.
                    </strong>{' '}
                    Es una promesa que nos hicimos y no hemos cumplido.
                  </p>
                )}
                {paradas > 0 && (
                  <p className="tenue">
                    Y {cuenta(paradas, 'lead', 'leads')} sin tocar en tres
                    semanas. Los que llevan cinco en «demo» es porque nadie
                    volvió a llamar.
                  </p>
                )}
              </div>
            );
          })()}

          <div className="tabla-marco">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Etapa</th>
                  <th className="num">Leads</th>
                  <th className="num">Acciones vencidas</th>
                  <th className="num">Parados</th>
                  <th>Próxima acción</th>
                </tr>
              </thead>
              <tbody>
                {/*
                  En el orden del embudo y no en el que venga: contactado → demo →
                  prueba → ganado o perdido. Es lo que lo hace legible como embudo.
                */}
                {ETAPAS.map((et) => {
                  const fila = embudo.data.stages.find(
                    (s) => s.stage === et.id
                  );
                  return (
                    <tr key={et.id}>
                      <td>
                        <button
                          type="button"
                          className="enlace-fila"
                          onClick={() => setEtapa(etapa === et.id ? '' : et.id)}
                        >
                          {et.etiqueta}
                        </button>
                      </td>
                      <td className="num">{fila?.leads ?? 0}</td>
                      <td className="num">
                        {fila && fila.overdue_actions > 0 ? (
                          <strong>{fila.overdue_actions}</strong>
                        ) : (
                          0
                        )}
                      </td>
                      <td className="num">{fila?.stale ?? 0}</td>
                      <td className="tenue">
                        {fila?.next_action ? fecha(fila.next_action) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <p className="tenue">
            {embudo.data.total_open === 0
              ? 'Ningún lead abierto.'
              : `${cuenta(embudo.data.total_open, 'lead abierto', 'leads abiertos')}.`}{' '}
            Este mes: {cuenta(embudo.data.won_this_month, 'ganado', 'ganados')}{' '}
            y {cuenta(embudo.data.lost_this_month, 'perdido', 'perdidos')}, que
            convierte el {numero(embudo.data.conversion_rate * 100, 0)} %.{' '}
            {/*
              Decir sobre qué se calcula, porque la cifra se va a copiar: sobre los
              cerrados del mes, no sobre el embudo entero. Dividir entre todos daría
              una conversión que baja cada vez que entra un lead nuevo.
            */}
            <strong>Sobre los cerrados del mes</strong>, no sobre el embudo
            entero.
          </p>

          {embudo.data.by_source.length > 0 && (
            <p className="tenue">
              Por canal:{' '}
              {embudo.data.by_source
                .map(
                  (s) =>
                    `${etiquetaDeCanal(s.source)} (${cuenta(s.open, 'abierto', 'abiertos')}, ${cuenta(s.won, 'ganado', 'ganados')})`
                )
                .join(' · ')}
              .
            </p>
          )}
        </>
      )}

      {/* ── La lista ──────────────────────────────────────────────────────── */}
      <h2 className="seccion">Los leads</h2>

      <div className="filtros">
        <label>
          Etapa
          <select value={etapa} onChange={(e) => setEtapa(e.target.value)}>
            <option value="">Todas</option>
            {ETAPAS.map((e) => (
              <option key={e.id} value={e.id}>
                {e.etiqueta}
              </option>
            ))}
          </select>
        </label>
      </div>

      {leads.isPending && <Cargando filas={5} etiqueta="Cargando los leads" />}

      {leads.isError && (
        <PanelError
          problema={aProblema(leads.error)}
          reintentar={() => void leads.refetch()}
        />
      )}

      {leads.data &&
        (leads.data.length === 0 ? (
          <Vacio
            titulo="Ningún lead con ese filtro"
            explicacion="Un lead se anota cuando un club pregunta: por el formulario de la web, en un campeonato o porque alguien nos lo recomendó."
          />
        ) : (
          <ul className="papeles">
            {leads.data.map((l) => {
              const vencida = accionVencida(l.next_action_on, hoy());
              return (
                <li key={l.id} className={vencida ? 'fila-marcada' : undefined}>
                  <div className="aviso-cabeza">
                    <strong>{l.club_name}</strong>
                    <span className={`pastilla ${claseDeEtapa(l.stage)}`}>
                      {etiquetaDeEtapa(l.stage)}
                    </span>
                    {l.source && (
                      <span className="pastilla pastilla-left">
                        {etiquetaDeCanal(l.source)}
                      </span>
                    )}
                    {/* La web la pide como opcional: aquí se ve que falta. */}
                    {!l.city && !l.academy_id && !etapaCerrada(l.stage) && (
                      <span className="pastilla pastilla-paused">
                        Sin ciudad
                      </span>
                    )}
                  </div>

                  <p className="tenue">
                    {[
                      l.contact_name,
                      l.email,
                      l.phone,
                      l.city,
                      l.discipline_codes.map(nombreDe).join(', '),
                      l.students_range && `${l.students_range} alumnos`,
                    ]
                      .filter(Boolean)
                      .join(' · ') || 'Sin datos de contacto'}
                  </p>

                  {/*
                    `days_since_update` es `null` en un lead cerrado, y ahí no se
                    pinta: «cerrado hace 40 días» al lado de los parados sería ruido.
                  */}
                  {!etapaCerrada(l.stage) && l.days_since_update !== null && (
                    <p className="tenue">
                      {l.days_since_update === 0
                        ? 'Tocado hoy'
                        : `Sin tocar ${cuenta(l.days_since_update, 'día', 'días')}`}
                      {l.next_action_on && (
                        <>
                          {' · '}
                          {vencida ? (
                            <strong>
                              la próxima acción era el {fecha(l.next_action_on)}
                            </strong>
                          ) : (
                            `próxima acción el ${fecha(l.next_action_on)}`
                          )}
                        </>
                      )}
                    </p>
                  )}

                  {l.stage === 'perdido' && (
                    <p className="tenue">
                      {l.lost_reason
                        ? `Se perdió: ${l.lost_reason}`
                        : 'Se perdió, sin motivo anotado.'}
                    </p>
                  )}

                  <div className="aviso-pie">
                    <span className="tenue">
                      {l.academy_id ? (
                        <>
                          Ya es cliente:{' '}
                          <button
                            type="button"
                            className="enlace-fila"
                            onClick={() => alAbrirClub(l.academy_id!)}
                          >
                            {l.academy_name ?? 'ver el club'}
                          </button>
                        </>
                      ) : (
                        `Anotado el ${fecha(l.created_at)}`
                      )}
                    </span>
                    <div className="hueco-acciones">
                      {/*
                        Convertir sale en un lead de prueba o ganado y sin club: es
                        lo que evita el error caro —dar de alta el club a mano y
                        dejar el lead en «demo» para siempre—.
                      */}
                      {(l.stage === 'prueba' || l.stage === 'ganado') &&
                        l.academy_id === null && (
                          <button
                            type="button"
                            onClick={() => setConvirtiendo(l)}
                          >
                            Convertir en club
                          </button>
                        )}
                      <button
                        type="button"
                        className="btn-plano"
                        onClick={() => {
                          setProblema(null);
                          setAbierto(abierto?.id === l.id ? null : l);
                        }}
                      >
                        {abierto?.id === l.id ? 'Cerrar' : 'Abrir'}
                      </button>
                    </div>
                  </div>

                  {abierto?.id === l.id && (
                    <FichaDeLead
                      lead={abierto}
                      guardando={mover.isPending}
                      alMover={(cambio) => {
                        setProblema(null);
                        mover.mutate({ id: l.id, ...cambio });
                      }}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        ))}
    </section>
  );
}

/** Lo que se cambia de un lead desde su ficha. */
interface CambioDeLead {
  stage?: EtapaDeLead;
  lost_reason?: string | null;
  next_action_on?: string | null;
  notes?: string | null;
  contact_name?: string | null;
  email?: string | null;
  phone?: string | null;
  city?: string | null;
  province?: string | null;
  discipline_codes?: string[];
}

const oNulo = (v: string) => (v.trim() === '' ? null : v.trim());

/**
 * Mover de etapa, apuntar la próxima llamada, dejar una nota **y completar sus
 * datos** · 03-11-2026. Hasta hoy los datos de contacto solo se escribían al anotar
 * el lead a mano: uno que llegaba por la web sin ciudad se quedaba sin ella, y al
 * convertirlo el club nacía sin sede.
 */
function FichaDeLead({
  lead,
  guardando,
  alMover,
}: {
  lead: Lead;
  guardando: boolean;
  alMover: (cambio: CambioDeLead) => void;
}) {
  const [contacto, setContacto] = useState(lead.contact_name ?? '');
  const [correo, setCorreo] = useState(lead.email ?? '');
  const [telefono, setTelefono] = useState(lead.phone ?? '');
  const [ciudad, setCiudad] = useState(lead.city ?? '');
  const [provincia, setProvincia] = useState(lead.province ?? '');
  const [codigos, setCodigos] = useState<string[]>([...lead.discipline_codes]);
  const [etapa, setEtapa] = useState<EtapaDeLead>(lead.stage);
  const [motivo, setMotivo] = useState(lead.lost_reason ?? '');
  /*
   * **`fechaParaInput` y no `String(...).slice(0, 10)`.** Eden deserializa un campo
   * con `format: 'date'` a un `Date`, así que el recorte daba «Thu Sep 1» y el
   * `<input type="date">` se quedaba vacío: el formulario borraba la próxima acción
   * de cualquier lead que ya la tuviera, sin decir nada.
   *
   * Está escrito en la cabecera de `formato.ts` desde el primer día y aun así se
   * cuela, porque en un fixture de prueba `next_action_on` es una cadena y funciona.
   * 18-09-2026.
   */
  const [cuando, setCuando] = useState(fechaParaInput(lead.next_action_on));
  const [notas, setNotas] = useState(lead.notes ?? '');

  return (
    /*
     * `.formulario` y no `.hueco`: `.hueco` lleva `text-align: center` porque es el
     * contenedor de los estados vacíos, y con él las etiquetas salían centradas
     * encima de sus campos y «Notas» al lado de un cuadro estrecho. Una clase que se
     * llama «hueco» describe un sitio, no una forma, y por eso engaña al reusarla.
     */
    <div className="formulario">
      <div className="rejilla">
        <label>
          En qué va
          <select
            value={etapa}
            onChange={(e) => setEtapa(e.target.value as EtapaDeLead)}
          >
            {ETAPAS.map((e) => (
              <option key={e.id} value={e.id}>
                {e.etiqueta}
              </option>
            ))}
          </select>
        </label>

        <label>
          Cuándo hay que volver
          <input
            type="date"
            value={cuando}
            onChange={(e) => setCuando(e.target.value)}
          />
        </label>
      </div>

      <div className="rejilla">
        <label>
          Contacto
          <input
            maxLength={120}
            value={contacto}
            onChange={(e) => setContacto(e.target.value)}
          />
        </label>
        <label>
          Correo
          <input
            type="email"
            maxLength={200}
            value={correo}
            onChange={(e) => setCorreo(e.target.value)}
          />
        </label>
        <label>
          Teléfono
          <input
            maxLength={30}
            value={telefono}
            onChange={(e) => setTelefono(e.target.value)}
          />
        </label>
        <label>
          Ciudad
          <input
            maxLength={80}
            placeholder="Donde entrenan"
            value={ciudad}
            onChange={(e) => setCiudad(e.target.value)}
          />
        </label>
        <label>
          Provincia
          <input
            maxLength={80}
            value={provincia}
            onChange={(e) => setProvincia(e.target.value)}
          />
        </label>
      </div>

      <CasillasDeDisciplinas marcadas={codigos} alCambiar={setCodigos} />

      {lead.academy_id === null &&
        (ciudad.trim() === '' || codigos.length === 0) && (
          <p className="nota">
            <strong>
              {ciudad.trim() === '' && codigos.length === 0
                ? 'Faltan la ciudad y lo que enseña.'
                : ciudad.trim() === ''
                  ? 'Falta la ciudad.'
                  : 'Falta lo que enseña.'}
            </strong>{' '}
            Pregúntaselo en la llamada: al dar de alta el club, la ciudad será
            su primera sede y las disciplinas, las suyas. Así su dueño no tendrá
            que crearlas.
          </p>
        )}

      {etapa === 'perdido' && (
        <label>
          Por qué se perdió
          <input
            maxLength={300}
            placeholder="Se quedó con la hoja de cálculo"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
          />
        </label>
      )}

      <label>
        Notas
        <textarea
          rows={3}
          maxLength={4000}
          value={notas}
          onChange={(e) => setNotas(e.target.value)}
        />
      </label>

      {etapa === 'perdido' && motivo.trim() === '' && (
        <p className="tenue">
          {/*
            Se pide y no se obliga, y es del contrato: exigir el motivo a las ocho de
            la tarde consigue que nadie mueva la etapa, y un embudo desactualizado es
            peor que uno con motivos flojos.
          */}
          Sin motivo también se guarda. Pero dentro de seis meses, «se perdió»
          sin más no enseña nada.
        </p>
      )}

      {(etapa === 'prueba' || etapa === 'ganado') &&
        lead.academy_id === null && (
          <p className="tenue">
            En «de prueba» o «ganado» aparece el botón de dar de alta su club.{' '}
            <strong>
              Dar de alta el club por otro lado deja este lead colgado
            </strong>{' '}
            y el embudo miente.
          </p>
        )}

      <div className="acciones">
        <button
          type="button"
          disabled={guardando}
          onClick={() =>
            alMover({
              stage: etapa,
              lost_reason: oNulo(motivo),
              next_action_on: cuando === '' ? null : cuando,
              notes: oNulo(notas),
              contact_name: oNulo(contacto),
              email: oNulo(correo),
              phone: oNulo(telefono),
              city: oNulo(ciudad),
              province: oNulo(provincia),
              discipline_codes: codigos,
            })
          }
        >
          {guardando ? 'Guardando…' : 'Guardar'}
        </button>
      </div>
    </div>
  );
}

/** Anotar un lead nuevo. Lo mínimo: cómo se llama el club. */
function AltaDeLead({
  alCerrar,
  alCrear,
}: {
  alCerrar: () => void;
  alCrear: () => void;
}) {
  const [nombre, setNombre] = useState('');
  const [contacto, setContacto] = useState('');
  const [correo, setCorreo] = useState('');
  const [telefono, setTelefono] = useState('');
  const [ciudad, setCiudad] = useState('');
  const [codigos, setCodigos] = useState<string[]>([]);
  const [canal, setCanal] = useState<CanalDeLead>('contacto');
  const [cuando, setCuando] = useState('');
  const [notas, setNotas] = useState('');
  const [problema, setProblema] = useState<Problema | null>(null);

  const crear = useMutation({
    mutationFn: async () => {
      const r = await api.v1.admin.leads.post({
        club_name: nombre.trim(),
        contact_name: contacto.trim() === '' ? null : contacto.trim(),
        email: correo.trim() === '' ? null : correo.trim(),
        phone: telefono.trim() === '' ? null : telefono.trim(),
        city: ciudad.trim() === '' ? null : ciudad.trim(),
        discipline_codes: codigos,
        source: canal,
        next_action_on: cuando === '' ? null : cuando,
        notes: notas.trim() === '' ? null : notas.trim(),
      });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: alCrear,
    onError: (e) => setProblema(aProblema(e)),
  });

  return (
    <form
      className="formulario"
      onSubmit={(e) => {
        e.preventDefault();
        setProblema(null);
        crear.mutate();
      }}
    >
      <div className="titulo-fila">
        <button type="button" className="btn-plano" onClick={alCerrar}>
          ← Comercial
        </button>
      </div>

      <h1>Anotar un lead</h1>

      <p className="tenue">
        Lo único imprescindible es cómo se llama el club. El resto se completa
        cuando se sepa: un lead a medias apuntado vale más que uno bien apuntado
        la semana que viene.
      </p>

      <div className="rejilla">
        <label>
          Club
          <input
            required
            maxLength={120}
            placeholder="Dojang Pascual"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
          />
        </label>

        <label>
          Con quién hablamos
          <input
            maxLength={120}
            placeholder="Manuel Pascual"
            value={contacto}
            onChange={(e) => setContacto(e.target.value)}
          />
        </label>

        <label>
          Correo
          <input
            type="email"
            maxLength={200}
            value={correo}
            onChange={(e) => setCorreo(e.target.value)}
          />
        </label>

        <label>
          Teléfono
          <input
            maxLength={30}
            value={telefono}
            onChange={(e) => setTelefono(e.target.value)}
          />
        </label>

        <label>
          Ciudad
          <input
            maxLength={80}
            value={ciudad}
            onChange={(e) => setCiudad(e.target.value)}
          />
        </label>

        <label>
          De dónde sale
          <select
            value={canal}
            onChange={(e) => setCanal(e.target.value as CanalDeLead)}
          >
            {CANALES.map((c) => (
              <option key={c.id} value={c.id}>
                {c.etiqueta}
              </option>
            ))}
          </select>
        </label>

        <label>
          Cuándo hay que llamar
          <input
            type="date"
            value={cuando}
            onChange={(e) => setCuando(e.target.value)}
          />
        </label>
      </div>

      <CasillasDeDisciplinas marcadas={codigos} alCambiar={setCodigos} />

      <label>
        Notas
        <textarea
          rows={3}
          maxLength={4000}
          placeholder="Tiene 80 alumnos y lleva las cuotas en un cuaderno."
          value={notas}
          onChange={(e) => setNotas(e.target.value)}
        />
      </label>

      <p className="tenue">
        {/* El canal es lo que sostiene las métricas de adquisición. */}
        <strong>El canal se anota ahora o no se anota nunca</strong>: es lo que
        permite saber dentro de un año qué trajo a los clubes que siguen aquí.
      </p>

      {problema && <PanelError problema={problema} />}

      <div className="acciones">
        <button type="submit" disabled={crear.isPending}>
          {crear.isPending ? 'Anotando…' : 'Anotar el lead'}
        </button>
        <button
          type="button"
          className="btn-plano"
          disabled={crear.isPending}
          onClick={alCerrar}
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}

/**
 * Dar de alta el club de un lead de prueba o ganado.
 *
 * Lo que ya está en el lead no se vuelve a teclear. Solo hace falta lo que el lead no
 * puede saber: **la URL del club** —se decide una vez y es por la que aparecerá en
 * Google— y **a qué dirección se invita al dueño**.
 *
 * **Y la ciudad, que es su primera sede** · 03-11-2026. Viene del lead; si no la
 * tiene, se pide aquí. Sin ella el club nace sin sede, que es lo que le pasó al
 * primero: su dueño fue a crear un grupo y no tenía dónde.
 */
function ConversionDeLead({
  lead,
  alCerrar,
  alConvertir,
}: {
  lead: Lead;
  alCerrar: () => void;
  alConvertir: () => void;
}) {
  const [url, setUrl] = useState(() => comoUrl(lead.club_name));
  const [correo, setCorreo] = useState(lead.email ?? '');
  const [nombre, setNombre] = useState(lead.contact_name?.split(' ')[0] ?? '');
  const [ciudad, setCiudad] = useState(lead.city ?? '');
  const [codigos, setCodigos] = useState<string[]>([...lead.discipline_codes]);
  const catalogo = useCatalogoDeDisciplinas();
  const [problema, setProblema] = useState<Problema | null>(null);

  const convertir = useMutation({
    mutationFn: async () => {
      const r = await api.v1.admin.leads({ id: lead.id }).convert.post({
        slug: url.trim(),
        owner_email: correo.trim(),
        owner_first_name: oNulo(nombre),
        city: oNulo(ciudad),
        // Siempre explícitas: lo que se ve marcado es lo que tendrá el club.
        discipline_ids: (catalogo.data ?? [])
          .filter((d) => codigos.includes(d.code))
          .map((d) => d.id),
      });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: alConvertir,
    onError: (e) => setProblema(aProblema(e)),
  });

  const urlMal =
    url.trim() !== '' && !/^[a-z0-9]+(-[a-z0-9]+)*$/.test(url.trim());

  return (
    <form
      className="formulario"
      onSubmit={(e) => {
        e.preventDefault();
        setProblema(null);
        convertir.mutate();
      }}
    >
      <div className="titulo-fila">
        <button type="button" className="btn-plano" onClick={alCerrar}>
          ← Comercial
        </button>
      </div>

      <h1>Dar de alta «{lead.club_name}»</h1>

      <div className="aviso" role="status">
        <p>
          <strong>Esto crea el club de verdad</strong> e invita al dueño por
          correo: el club, su dueño y los datos de arranque. El contrato de
          encargo lo firma el dueño desde su panel.
        </p>
        <p className="tenue">
          Y enlaza este lead con el club en la misma operación. Dar de alta el
          club por otro lado es el error más caro del CRM: el lead se queda en
          su etapa para siempre y el embudo entero miente.
        </p>
      </div>

      <div className="rejilla">
        <label>
          La URL del club
          <input
            required
            maxLength={60}
            placeholder="dojang-pascual"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
        </label>

        <label>
          Correo del dueño
          <input
            type="email"
            required
            maxLength={200}
            value={correo}
            onChange={(e) => setCorreo(e.target.value)}
          />
        </label>

        <label>
          Su nombre
          <input
            maxLength={80}
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
          />
        </label>

        <label>
          Ciudad de su sede
          <input
            maxLength={80}
            placeholder="Donde entrenan"
            value={ciudad}
            onChange={(e) => setCiudad(e.target.value)}
          />
        </label>
      </div>

      <CasillasDeDisciplinas marcadas={codigos} alCambiar={setCodigos} />

      {ciudad.trim() === '' ? (
        <p className="nota">
          <strong>Sin ciudad, el club nace sin sede</strong> y su dueño tendrá
          que crearla antes de su primer grupo. Si la sabes, escríbela: será su
          «Sede principal» y él solo completará la dirección.
        </p>
      ) : (
        <p className="tenue">
          Nacerá con <strong>«Sede principal» en {ciudad.trim()}</strong>. La
          calle la pone su dueño desde el panel, que se la pedirá.
        </p>
      )}

      {codigos.length === 0 && (
        <p className="nota">
          <strong>Sin disciplina, el club nace sin ninguna</strong> y su dueño
          tendrá que elegirla en <em>El club → Disciplinas</em>. Si sabes qué
          enseña, márcalo: su primer grupo saldrá con ella ya elegida.
        </p>
      )}

      <p className="tenue">
        {urlMal ? (
          <strong>
            La URL va en minúsculas, con números y guiones: «dojang-pascual».
            Sin acentos ni espacios, porque es una dirección web.
          </strong>
        ) : (
          <>
            <strong>La URL se decide una vez</strong>: es por la que el club
            aparecerá en Google, y cambiarla después rompe los enlaces que haya
            repartido. Lo demás —teléfono, correo, canal— ya está en el lead y
            viaja solo.
            <br />
            Al darlo de alta,{' '}
            <strong>le llega un correo para entrar en su panel</strong> y el
            club nace «de prueba». Sus días de prueba empiezan a contar cuando
            le des plan en su ficha, en «Lo contratado».{' '}
            {lead.stage === 'ganado'
              ? 'El lead ya está en «ganado» y se queda ahí.'
              : 'El lead queda en «de prueba» y pasa a «ganado» cuando se anote su primer pago.'}
          </>
        )}
      </p>

      {problema && <PanelError problema={problema} />}

      <div className="acciones">
        <button
          type="submit"
          disabled={
            convertir.isPending || urlMal || url.trim() === '' || !catalogo.data
          }
        >
          {convertir.isPending ? 'Dando de alta…' : 'Dar de alta el club'}
        </button>
        <button
          type="button"
          className="btn-plano"
          disabled={convertir.isPending}
          onClick={alCerrar}
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}
